/** GeoJSON position: [longitude, latitude] */
export type Position = [number, number];

/** Structured address query for the Azure Maps Geocoding API */
export interface GeocodeAddressRequest {
	addressLine?: string;
	locality?: string;
	adminDistrict?: string;
	postalCode?: string;
}

/** Subset of the Azure Maps Geocoding response (api-version 2025-01-01) read by the app */
export interface GeocodingResponseRaw {
	features: {
		geometry: { type: 'Point'; coordinates: number[] };
		properties: {
			confidence?: 'High' | 'Medium' | 'Low';
			address?: { formattedAddress?: string };
		};
	}[];
}

export interface GeocodedAddress {
	position: Position;
	formattedAddress?: string;
	confidence?: 'High' | 'Medium' | 'Low';
}

/** Azure Maps vehicleSpec.loadType values for US routing (hazmat classes 1-9) */
export type VehicleLoadType = `USHazmatClass${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`;

export interface RouteDirectionsRequest {
	origin: Position;
	destination: Position;
	loadType?: VehicleLoadType[];
}

/**
 * Maps Trinium hazmat class codes to Azure Maps load types. Trinium stores the DOT class with its
 * division ('1.4G', '2.1', '3.0'); Azure only distinguishes the whole class, so the leading digit is kept.
 */
export function toVehicleLoadTypes(hazmatClassIds: string[]): VehicleLoadType[] {
	const loadTypes = new Set<VehicleLoadType>();

	for (const classId of hazmatClassIds) {
		const hazmatClass = parseInt(classId, 10);
		if (hazmatClass >= 1 && hazmatClass <= 9) {
			loadTypes.add(`USHazmatClass${String(hazmatClass)}` as VehicleLoadType);
		}
	}

	return [...loadTypes];
}

interface RouteWaypointFeatureRaw {
	geometry: { type: 'Point'; coordinates: number[] };
	properties: { type: 'Waypoint' };
}

interface RoutePathFeatureRaw {
	geometry: { type: 'MultiLineString'; coordinates: number[][][] };
	properties: {
		type: 'RoutePath';
		distanceInMeters: number;
		durationInSeconds: number;
		durationTrafficInSeconds?: number;
	};
}

/** Subset of the Azure Maps Route Directions response (api-version 2025-01-01) read by the app */
export interface RouteDirectionsResponseRaw {
	features: (RouteWaypointFeatureRaw | RoutePathFeatureRaw)[];
}

export interface DriverRoute {
	/** Route path positions in travel order */
	path: Position[];
	distanceInMeters: number;
	durationInSeconds: number;
}

/** The first match, or null when the geocoder found nothing for the address */
export function transformGeocodingResponse(result: GeocodingResponseRaw): GeocodedAddress | null {
	const feature = result.features.at(0);
	if (!feature) {
		return null;
	}

	const [longitude, latitude] = feature.geometry.coordinates;

	return {
		position: [longitude, latitude],
		formattedAddress: feature.properties.address?.formattedAddress,
		confidence: feature.properties.confidence
	};
}

/** Flattens the route's MultiLineString into one path, or null when no route came back */
export function transformRouteDirectionsResponse(result: RouteDirectionsResponseRaw): DriverRoute | null {
	const routePath = result.features.find((feature): feature is RoutePathFeatureRaw => feature.properties.type === 'RoutePath');
	if (!routePath) {
		return null;
	}

	return {
		path: routePath.geometry.coordinates.flat().map(([longitude, latitude]) => [longitude, latitude]),
		distanceInMeters: routePath.properties.distanceInMeters,
		durationInSeconds: routePath.properties.durationInSeconds
	};
}
